<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint"/>

    <!-- 가. 조사 개요 -->
    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot" /> 가. 조사 개요</h5>
        </vs-col>
      </vs-row>
      <div class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
        <div class="col-span-4 flex items-center">
          <div class="label-text mr-4">작업공정명</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.inputA_1 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputA_1" class="w-full flex-1" />
        </div>
        <div class="col-span-4 flex items-center">
          <div class="label-text mr-4">작업명</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.inputA_2 }}</p>
          <ml-input :readonly="board.readonly" v-else v-model="form.inputA_2" class="w-full flex-1" />
        </div>
        <div class="col-span-4 flex items-center">
          <div class="label-text mr-4">조사일</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.inputA_3 }}</p>
          <date-selector v-else :readonly="board.readonly" v-model="form.inputA_3" class="w-full flex-1" />
        </div>
        <div class="col-span-4 flex items-center">
          <div class="label-text mr-4">재해형태(필수)</div>
          <p v-if="isPrint" class="print-input">{{ form.inputA_4 }}</p>
          <disaster-class-selector
            v-else
            class="w-full flex-1"
            v-model="form.inputA_4"
            :readonly="board.readonly"
            :custom-disaster-list="customDisasterList"
            multiple
          />
        </div>
      </div>
    </div>

    <!-- 나. 작업장 상황 조사 -->
    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot" /> 나. 작업장 상황 조사</h5>
        </vs-col>
      </vs-row>
      <radio-text-input
        :readonly="board.readonly"
        :isPrint="isPrint"
        title="작업 설비"
        radioName="radio_1"
        :renderData="[
          { textInput: false, value: '변화없음' },
          { textInput: true, value: '변화 있음 (언제부터)' },
        ]"
        v-model="form.inputB_1"
      />
      <radio-text-input
        :readonly="board.readonly"
        :isPrint="isPrint"
        title="작업량"
        radioName="radio_2"
        :renderData="[
          { textInput: false, value: '변화없음' },
          { textInput: true, value: '줄어듦 (언제부터)' },
          { textInput: true, value: '늘어남 (언제부터)' },
          { textInput: true, value: '기타 (언제부터)' },
        ]"
        v-model="form.inputB_2"
      />
      <radio-text-input
        :readonly="board.readonly"
        :isPrint="isPrint"
        title="작업 속도"
        radioName="radio_3"
        :renderData="[
          { textInput: false, value: '변화없음' },
          { textInput: true, value: '줄어듦 (언제부터)' },
          { textInput: true, value: '늘어남 (언제부터)' },
          { textInput: true, value: '기타 (언제부터)' },
        ]"
        v-model="form.inputB_3"
      />
      <radio-text-input
        :readonly="board.readonly"
        :isPrint="isPrint"
        title="업무 변화"
        radioName="radio_4"
        :renderData="[
          { textInput: false, value: '변화없음' },
          { textInput: true, value: '줄어듦 (언제부터)' },
          { textInput: true, value: '늘어남 (언제부터)' },
          { textInput: true, value: '기타 (언제부터)' },
        ]"
        v-model="form.inputB_4"
      />
    </div>

    <!-- 다. 작업조건 조사(인간공학적인 측면을 고려한 조사) -->
    <div>
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot" /> 다. 작업조건 조사(인간공학적인 측면을 고려한 조사)</h5>
        </vs-col>
      </vs-row>

      <!-- 1단계 : 작업별 주요 작업내용 (유해요인 조사자) -->
      <table-custom
        :board="board"
        :is-print="isPrint"
        :isTitleIcon="false"
        title="1단계 : 작업별 주요 작업내용 (유해요인 조사자)"
        :tableData="[
          { id: 'cell1', title: '작업명', type: 'text', placeholder: '내용' },
          { id: 'cell2', title: '작업내용(단위작업명)', type: 'text', placeholder: '내용' },
        ]"
        v-model="form.inputC_1"
      />

      <!-- 2단계 : 작업별 작업부하 및 작업빈도 (근로자 면담) -->
      <table-custom
        :board="board"
        :is-print="isPrint"
        :isTitleIcon="false"
        title="2단계 : 작업별 작업부하 및 작업빈도 (근로자 면담)"
        :tableData="[
          { id: 'cell1', title: '단위작업명', type: 'text', placeholder: '내용' },
          {
            id: 'cell2',
            title: '부담작업(호)',
            type: 'select',
            selectData: ['1호', '2호', '3호', '4호', '5호', '6호', '7호', '8호', '9호', '10호', '11호'],
            placeholder: '선택',
          },
          {
            id: 'cell3',
            title: '작업부하(A)',
            type: 'select',
            selectData: ['1', '2', '3', '4', '5'],
            placeholder: '선택',
          },
          {
            id: 'cell4',
            title: '작업부하(B)',
            type: 'select',
            selectData: ['1', '2', '3', '4', '5'],
            placeholder: '선택',
          },
          { id: 'cell5', title: '총 점수(AxB)', type: 'calcul', placeholder: '자동계산', readonly: true },
        ]"
        v-model="form.inputC_2"
      >
        <template #customControlBtn>
          <p class="flex items-center" style="cursor: pointer; color: #7e72f2" @click="openPopup"><feather-icon icon="AlertCircleIcon" style="width: 18px; margin-right: 3px" />작업별 작업부하 및 작업빈도</p>
        </template>
        <template #calcul="{ data, data_i }">
          <ml-input readonly :value="form.inputC_2[data_i].cell5" placeholder="자동계산" class="w-full flex-1" @click.stop />
        </template>
      </table-custom>

      <!-- 부담작업(호) - 근골격계 -->
      <div class="mb-5">
        <p class="sub-title mb-5">부담작업(호) - 근골격계</p>
        <table class="table-1">
          <tr>
            <td>
              <p>구분</p>
              <p class="flex items-center font-normal"><feather-icon icon="AlertCircleIcon" style="width: 18px; margin-right: 3px" />작업 그림 클릭 시 작업 해설을 확인할 수 있습니다.</p>
            </td>
            <td class="font-bold">
              ①
              <div><img src="@/assets/images/부담작업/1.png" /></div>
            </td>
            <td class="font-bold">
              ②
              <div><img src="@/assets/images/부담작업/2.png" /></div>
            </td>
            <td class="font-bold">
              ③
              <div><img src="@/assets/images/부담작업/3.png" /></div>
            </td>
            <td class="font-bold">
              ④
              <div><img src="@/assets/images/부담작업/4.png" /></div>
            </td>
            <td class="font-bold">
              ⑤
              <div><img src="@/assets/images/부담작업/5.png" /></div>
            </td>
            <td class="font-bold">
              ⑥
              <div><img src="@/assets/images/부담작업/6.png" /></div>
            </td>
            <td class="font-bold">
              ⑦
              <div><img src="@/assets/images/부담작업/7.png" /></div>
            </td>
            <td class="font-bold">
              ⑧
              <div><img src="@/assets/images/부담작업/8.png" /></div>
            </td>
            <td class="font-bold">
              ⑨
              <div><img src="@/assets/images/부담작업/9.png" /></div>
            </td>
            <td class="font-bold">
              ⑩
              <div><img src="@/assets/images/부담작업/10.png" /></div>
            </td>
            <td class="font-bold">
              ⑪
              <div><img src="@/assets/images/부담작업/11.png" /></div>
            </td>
          </tr>
          <tr>
            <td>노출시간</td>
            <td>하루에 총 4시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td>하루에 총 2시간 이상</td>
            <td>하루에 총 2시간 이상</td>
          </tr>
          <tr>
            <td>노출빈도</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td>하루에 총 10회 이상</td>
            <td>하루에 총 25회 이상</td>
            <td>분당 2회 이상</td>
            <td>시간당 10회 이상</td>
          </tr>
          <tr>
            <td>신체부위</td>
            <td>손, 손가락, 팔, 어깨</td>
            <td>목, 어깨,손목, 손, 팔꿈치</td>
            <td>어깨, 팔</td>
            <td>목, 허리</td>
            <td>다리, 무릎</td>
            <td>손가락</td>
            <td>손</td>
            <td>허리</td>
            <td>손, 무릎</td>
            <td>허리</td>
            <td>손, 무릎, 팔꿈치</td>
          </tr>
          <tr>
            <td>작업자세 및 내용</td>
            <td>집중적인 자료 입력 작업 (마우스, 키보드 사용)</td>
            <td>같은 동작 반복작업</td>
            <td>머리 위에 손, 팔꿈치가 몸통으로부터 들림, 팔꿈치를 몸통 뒤쪽에 위치</td>
            <td>구부리거나 비틈 (지지되지 않은 상태,자세변경불가)</td>
            <td>쪼그리고 앉거나 무릎을 굽힘</td>
            <td>한 손가락 집어 옮기거나 쥐는 작업 (지지되지 않은 상태)</td>
            <td>물건을 한손으로 들거나 잡는 작업</td>
            <td>물건을 드는 작업</td>
            <td>어깨 위에서 들기, 팔을 뻗은 상태에서 물건을 드는 작업</td>
            <td>물건을 드는 작업</td>
            <td>반복적인 충격</td>
          </tr>
          <tr>
            <td>무게</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td class="empty">-</td>
            <td>1kg이상의 물건 들기, 2kg이상에 상응하는 힘으로 쥐기</td>
            <td>4.5kg이상의 물건 들기, 동일한 힘으로집기</td>
            <td>25kg이상</td>
            <td>10kg이상</td>
            <td>4.5kg이상</td>
            <td class="empty">-</td>
          </tr>
        </table>
      </div>

      <!-- 3단계 : 유해요인평가 -->
      <div class="mb-5">
        <h5>3단계 : 유해요인평가</h5>
        <div class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
          <div class="col-span-4 flex items-center">
            <div class="label-text mr-4">작업명</div>
            <p v-if="isPrint" class="print-input flex-1">{{ form.inputC_3 }}</p>
            <ml-input :readonly="board.readonly" v-else v-model="form.inputC_3" class="w-full flex-1" />
          </div>
          <div class="col-span-4 flex items-center">
            <div class="label-text mr-4">근로자명</div>
            <p v-if="isPrint" class="print-input flex-1">{{ form.inputC_4 }}</p>
            <ml-input :readonly="board.readonly" v-else v-model="form.inputC_4" class="w-full flex-1" />
          </div>
        </div>
      </div>

      <!-- 사진대지 -->
      <p class="sub-title mb-5">사진대지</p>
      <photo-upload-grid-module :row-count="2" :grid-count="2" v-model="form.photoList" :is-print="isPrint" :readonly="board.readonly" placeholder="단위작업명" />

      <!-- 작업별로 관찰된 유해요인에 대한 원인분석 -->
      <table-custom
        :board="board"
        :is-print="isPrint"
        :isTitleIcon="false"
        title="작업별로 관찰된 유해요인에 대한 원인분석"
        :tableData="[
          { id: 'cell1', title: '단위작업명', type: 'text', placeholder: '내용' },
          {
            id: 'cell2',
            title: '부담작업(호)',
            type: 'select',
            selectData: ['1호', '2호', '3호', '4호', '5호', '6호', '7호', '8호', '9호', '10호', '11호'],
            placeholder: '선택',
          },
          {
            id: 'cell3',
            title: '유해요인',
            type: 'select',
            selectData: ['반복동작', '부자연스런, 부적절한 자세', '과도한 힘', '접촉스트레스', '진동', '기타요인'],
            placeholder: '선택',
          },
          { id: 'cell4', title: '발생 원인', type: 'text', placeholder: '내용' },
          { id: 'cell5', title: '비고', type: 'text', placeholder: '내용' },
        ]"
        v-model="form.inputC_5"
      />

      <!-- 유해요인 설명 -->
      <div class="mb-5">
        <h5 class="mb-5">유해요인 설명</h5>
        <table class="table-2">
          <thead>
            <tr>
              <th>유해요인</th>
              <th>설명</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>반복동작</td>
              <td>같은 근육, 힘줄 또는 관절을 사용하여 동일한 유형의 동작을 되풀이해서 수행함</td>
            </tr>
            <tr>
              <td>부자연스런, 부적절한 자세</td>
              <td>반복적이거나 지속적으로 팔을 뻗음, 비틂, 구부림, 머리 위 작업, 무릎을 꿇음, 쪼그림, 고정 자세를 유지함, 손가락으로 집기 등</td>
            </tr>
            <tr>
              <td>과도한 힘</td>
              <td>작업을 수행하기 위해 근육을 과도하게 사용함</td>
            </tr>
            <tr>
              <td>접촉스트레스</td>
              <td>작업대 모서리, 키보드, 작업공구, 가위사용 등으로 인해 손목, 손바닥, 팔 등이 지속적으로 눌리거나 손바닥 또는 무릎 등을 사용하여 반복적으로 물체에 압력을 가함으로써 해당 신체부위가 충격을 받게 되는 것</td>
            </tr>
            <tr>
              <td>진동</td>
              <td>지속적이거나 높은 강도의 손-팔 또는 몸 전체의 진동</td>
            </tr>
            <tr>
              <td>기타요인</td>
              <td>극심한 저온 또는 고온, 너무 밝거나 어두운 조명 등</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly" />

    <vs-popup class="popupSearchField" title="작업별 작업부하 및 작업빈도" :active.sync="showPopup">
      <table class="table-popup">
        <thead>
          <tr>
            <th>작업 부하(A)</th>
            <th>점수</th>
            <th>작업 빈도(B)</th>
            <th>점수</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>매우 쉬움</td>
            <td>1</td>
            <td>3개월마다(년 2~3회)</td>
            <td>1</td>
          </tr>
          <tr>
            <td>쉬움</td>
            <td>2</td>
            <td>가끔(하루 또는 주 2~3일에 1회)</td>
            <td>2</td>
          </tr>
          <tr>
            <td>약간 힘듦</td>
            <td>3</td>
            <td>자주(1일 4시간)</td>
            <td>3</td>
          </tr>
          <tr>
            <td>힘듦</td>
            <td>4</td>
            <td>계속(1일 4시간 이상)</td>
            <td>4</td>
          </tr>
          <tr>
            <td>매우 힘듦</td>
            <td>5</td>
            <td>초과근무 시간(1일 8시간 이상)</td>
            <td>5</td>
          </tr>
        </tbody>
      </table>
    </vs-popup>
  </div>
</template>

<script>
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import DateSelector from "@/components/selector/DateSelector";
import RadioTextInput from "@/components/radio/RadioTextInput";
import TableCustom from "@/components/TableCustom";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector.vue";
import {researchDisasters} from "@/util/DisasterDataResearch";

export default {
  components: {
    DisasterClassSelector,
    FieldSelectModule,
    FileUploadModule,
    DateSelector,
    RadioTextInput,
    TableCustom,
    PhotoUploadGridModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        value.title = this.board.writer.department;
        if (value.inputC_2) {
          for (const item of value.inputC_2) {
            const multiple = (item.cell3 || 0) * (item.cell4 || 0);
            if (item.cell5 && item.cell5 === multiple) {
              continue;
            }
            item.cell5 = multiple;
          }
        }

        this.$emit("input", value);
      },
    },
  },
  data() {
    return {
      form: {
        stdInfo: {},
        fileList: [],

        inputA_1: "",
        inputA_2: "",
        inputA_3: "",
        inputA_4: [],

        inputB_1: {},
        inputB_2: {},
        inputB_3: {},
        inputB_4: {},

        inputC_1: [],
        inputC_2: [],
        inputC_3: "",
        inputC_4: "",
        inputC_5: [],

        photoList: [],
      },
      temp_inputC_2: [],
      showPopup: false,
      customDisasterList: researchDisasters,
    };
  },
  methods: {
    openPopup() {
      this.showPopup = true;
    },
  },
};
</script>

<style scoped lang="scss">
.label-text {
  width: 114px;
  font-weight: 500;
  font-size: 14px;
  padding-right: 12px;
}
.sub-title {
  font-weight: 500;
  font-size: 16px;
}

table.table-1 {
  width: 100%;
  border-collapse: collapse;
  text-align: center;

  tr {
    td {
      width: 8%;
      border: 1px solid #cdcdcd;
      padding: 8px;

      & > div {
        position: relative;
        min-height: 90px;
        & > img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      &:first-child {
        width: 12%;
        font-weight: 500;
        background: #f9f9f9;
      }

      &.empty {
        background: #f9f9f9;
      }
    }
  }
}
table.table-2 {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    font-size: 14px;
    border: 1px solid #cdcdcd;
    padding: 8px;
  }

  th,
  tbody > tr > td:first-child {
    text-align: center;
    font-weight: 500;
    background: #f9f9f9;
  }

  th {
    &:first-child {
      width: 12%;
    }
  }
}

table.table-popup {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: center;
    font-size: 14px;
    border: 1px solid #cdcdcd;
    padding: 8px;
  }

  th {
    font-weight: 500;
    background: #f9f9f9;

    &:nth-child(2n + 1) {
      width: 40%;
    }
  }
}
</style>
